import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useImportApi } from "./importApi";
import {
	hiddenColumns,
	isNonImportColumn,
	optionIdByName,
	toastApiError,
	useMountedRef,
	visibleColumns,
} from "./common";
import UploadStep from "./UploadStep";
import MapColumnsStep from "./MapColumnsStep";
import GroupPreviewStep from "./GroupPreviewStep";
import ExecuteStep from "./ExecuteStep";
import "./Import.css";

const STEP_LABELS = ["Upload", "Map Columns", "Group & Preview", "Execute"];

const EMPTY_DEFAULTS = { campaign: "", status: "", source: "", group: "" };

// Batch defaults preselected by NAME from the live GET /import/iris/options
// lists (never by id). Missing name → left unselected.
const DEFAULT_STATUS_NAME = "Brand New";
const DEFAULT_GROUP_NAME = "ISO";

// Group-by fallback when no column is mapped to the `dba` special field.
const GROUP_BY_HEADER_FALLBACKS = ["company name", "company", "dba"];

// Same header normalization the backend's suggestion matching uses: separators
// to spaces, lowercase, minus the scrubber's new_/existing_ prefix.
function normalizeHeader(col) {
	return String(col ?? "")
		.replace(/[_\-./]+/g, " ")
		.trim()
		.replace(/\s+/g, " ")
		.toLowerCase()
		.replace(/^(new|existing)\s+/, "");
}

// A full-name column's Contacts split: {first, last} field ids, or null when the
// entry has none (saved templates from before the split existed never do).
function normalizeContactSplit(raw) {
	if (!raw || typeof raw !== "object") return null;
	const first = raw.first;
	const last = raw.last;
	if (first === null || first === undefined || last === null || last === undefined) {
		return null;
	}
	return { first, last };
}

// Suggested/template mapping entries may be {field_id, scope} or a bare id.
// `contact_field_id` (dual placement: primary row on the lead field, every row
// as a Contacts record), `contact_split` (the same idea for a single full-name
// column, split into the Contacts set's First/Last Name) and `via` (how the
// backend matched it — "weak_label" means "guess, please verify") ride along
// when present.
function normalizeMappingObject(raw) {
	const out = {};
	if (!raw || typeof raw !== "object") return out;
	Object.entries(raw).forEach(([col, val]) => {
		// Defense in depth: a never-import column (scrub metadata / a copy of
		// EXISTING Iris data) must never end up in the mapping state, even from
		// a suggestion the backend somehow let through or an old saved template.
		if (isNonImportColumn(col)) return;
		if (val === null || val === undefined) return;
		if (typeof val === "object") {
			if (val.field_id === null || val.field_id === undefined) return;
			const entry = {
				field_id: val.field_id,
				scope: val.scope === "contact" ? "contact" : "lead",
			};
			if (
				entry.scope === "lead" &&
				val.contact_field_id !== null &&
				val.contact_field_id !== undefined
			) {
				entry.contact_field_id = val.contact_field_id;
			}
			const split = entry.scope === "lead" ? normalizeContactSplit(val.contact_split) : null;
			if (split) entry.contact_split = split;
			if (val.via) entry.via = val.via;
			out[col] = entry;
		} else {
			out[col] = { field_id: val, scope: "lead" };
		}
	});
	return out;
}

function recordSetTabIdSet(catalog) {
	return new Set(
		(catalog?.record_set_tabs || []).map((t) => String(t?.id ?? t?.tab_id ?? t))
	);
}

function isRecordSetField(field, recordSetTabIds) {
	const tabId = field?.tab ?? field?.tab_id ?? field?.category ?? null;
	return (
		tabId !== null && tabId !== undefined && recordSetTabIds.has(String(tabId))
	);
}

// Normalize GET /import/iris/fields into a flat list the UI can search.
function normalizeCatalog(catalog) {
	if (!catalog) return { fields: [], fieldById: {} };
	const tabs = catalog.tabs || [];
	const tabNameById = {};
	tabs.forEach((t) => {
		const id = t?.id ?? t?.tab_id;
		if (id !== null && id !== undefined) {
			tabNameById[String(id)] = t.name ?? t.label ?? t.tab_name ?? `Tab ${id}`;
		}
	});
	const recordSetTabIds = recordSetTabIdSet(catalog);
	const fields = (catalog.fields || []).map((f) => {
		const tabId = f.tab ?? f.tab_id ?? f.category ?? null;
		return {
			id: f.id,
			label: f.label ?? f.name ?? `Field ${f.id}`,
			special: String(f.special ?? "").trim().toLowerCase(),
			tabName:
				tabId !== null && tabId !== undefined
					? tabNameById[String(tabId)] ?? String(tabId)
					: f.tab_name ?? "",
			isRecordSet: isRecordSetField(f, recordSetTabIds),
		};
	});
	const fieldById = {};
	fields.forEach((f) => {
		fieldById[String(f.id)] = f;
	});
	return { fields, fieldById };
}

// The Association field's live dropdown options, from the already-fetched
// GET /import/iris/fields catalog — discovered by label, not a hardcoded id
// (backend does the same at import time).
function associationOptionsFromCatalog(catalog) {
	if (!catalog) return [];
	const recordSetTabIds = recordSetTabIdSet(catalog);
	const field = (catalog.fields || []).find((f) => {
		const label = String(f?.label ?? f?.name ?? "").trim().toLowerCase();
		return label === "association" && !isRecordSetField(f, recordSetTabIds);
	});
	const dropdown = field?.options?.dropdown || [];
	const values = (Array.isArray(dropdown) ? dropdown : []).map((o) =>
		typeof o === "object" ? o.value ?? o.label ?? o.key : o
	);
	return values
		.map((v) => String(v ?? "").trim())
		.filter((v) => v !== "");
}

// The lead-level company-name field (`dba` special), discovered from the
// catalog — used to preselect the group-by column (Change 4).
function dbaFieldIdFromCatalog(catalog) {
	if (!catalog) return null;
	const recordSetTabIds = recordSetTabIdSet(catalog);
	const field = (catalog.fields || []).find(
		(f) =>
			String(f?.special ?? "").trim().toLowerCase() === "dba" &&
			!isRecordSetField(f, recordSetTabIds)
	);
	return field ? field.id : null;
}

// Default group-by column: whichever CSV column is mapped to the `dba` field,
// else a company_name/company/dba-looking header (prefix-stripped).
function suggestGroupByColumn(columns, mapping, dbaFieldId) {
	const cols = columns || [];
	if (dbaFieldId !== null && dbaFieldId !== undefined) {
		const mapped = Object.entries(mapping || {}).find(
			([col, entry]) =>
				cols.includes(col) && String(entry?.field_id) === String(dbaFieldId)
		);
		if (mapped) return mapped[0];
	}
	return (
		cols.find((c) => GROUP_BY_HEADER_FALLBACKS.includes(normalizeHeader(c))) ||
		null
	);
}

function normalizeIssue(iss) {
	if (typeof iss === "string") {
		// Unknown severity: treat as blocking (safe default — spec blocks Execute
		// while blocking issues exist).
		return { message: iss, blocking: true };
	}
	const level = String(iss?.level ?? iss?.severity ?? iss?.type ?? "")
		.toLowerCase();
	let blocking;
	if (typeof iss?.blocking === "boolean") blocking = iss.blocking;
	else if (level === "warning" || level === "warn" || level === "info")
		blocking = false;
	else blocking = true; // "error", "blocking", or unspecified
	return {
		message:
			iss?.message ?? iss?.detail ?? iss?.issue ?? JSON.stringify(iss ?? {}),
		blocking,
	};
}

// Normalize POST /import/preview output (shape not pinned by spec §5).
function normalizePreview(raw) {
	if (!raw || typeof raw !== "object") {
		return { groups: [], issues: [], blocking: false, totalContacts: 0 };
	}
	const rawGroups =
		raw.groups || raw.companies || raw.leads || raw.preview || [];
	const rawIssues = raw.issues || raw.validation_issues || raw.errors || [];

	const groups = (Array.isArray(rawGroups) ? rawGroups : []).map((g, i) => {
		const contacts = g?.contacts || g?.records || g?.rows || [];
		return {
			key: String(g?.group_key ?? g?.key ?? g?.company ?? `group-${i + 1}`),
			leadFields: g?.lead_fields || g?.fields || g?.lead || {},
			contacts: Array.isArray(contacts) ? contacts : [],
			issues: (Array.isArray(g?.issues) ? g.issues : []).map(normalizeIssue),
			// Change 1 (merged company spellings) / Change 2 (why this row is
			// primary) — both optional, absent when not applicable.
			mergedNames: Array.isArray(g?.merged_names) ? g.merged_names : null,
			primaryRowIndex: g?.primary_row_index ?? null,
			primaryReason: g?.primary_reason ?? null,
		};
	});
	const issues = (Array.isArray(rawIssues) ? rawIssues : []).map(
		normalizeIssue
	);
	const blocking =
		issues.some((i) => i.blocking) ||
		groups.some((g) => g.issues.some((i) => i.blocking));
	const totalContacts =
		raw.total_contacts ??
		raw.total_rows ??
		groups.reduce((n, g) => n + g.contacts.length, 0);
	return { groups, issues, blocking, totalContacts };
}

const coerceId = (v) => {
	if (v === "" || v === null || v === undefined) return null;
	return /^\d+$/.test(String(v)) ? Number(v) : v;
};

export default function ImportWizard({ onViewHistory, handoff = null }) {
	const api = useImportApi();
	const mountedRef = useMountedRef();

	const [step, setStep] = useState(0);
	const [upload, setUpload] = useState(null);
	const [fileName, setFileName] = useState("");
	const [catalogState, setCatalogState] = useState({
		status: "idle",
		data: null,
	});
	const [optionsState, setOptionsState] = useState({
		status: "idle",
		data: null,
	});
	const [mapping, setMapping] = useState({});
	const [groupBy, setGroupBy] = useState([]);
	const [primaryFlagColumn, setPrimaryFlagColumn] = useState("");
	const [defaults, setDefaults] = useState(EMPTY_DEFAULTS);
	const [association, setAssociation] = useState("");
	// Iris user the created leads get assigned to (POST /leads `users`). null =
	// no assignment at all, which is the default — nothing is sent.
	const [assignedUserId, setAssignedUserId] = useState(null);
	// Change 1: company matching mode. false (default) = normalized grouping
	// (ignores LLC/Inc/punctuation/case); true = strict exact-text match.
	const [strictGrouping, setStrictGrouping] = useState(false);
	const [preview, setPreview] = useState(null);
	const [previewLoading, setPreviewLoading] = useState(false);
	const [batchId, setBatchId] = useState(null);
	// Change 4: the group-by and named-default preselections are one-shot per
	// upload — once applied (or once the user has made their own choice), never
	// fight the user by re-applying them.
	const groupByPreselectedRef = useRef(false);
	const defaultsPreselectedRef = useRef(false);

	// Any change to the inputs invalidates a previously computed preview.
	useEffect(() => {
		setPreview(null);
	}, [mapping, groupBy, primaryFlagColumn, defaults, association, assignedUserId,
		strictGrouping]);

	// ---- Catalog (GET /import/iris/fields), fetched when mapping step opens ----
	const fetchCatalog = useCallback(() => {
		setCatalogState({ status: "loading", data: null });
		api
			.getIrisFields()
			.then((data) => {
				if (mountedRef.current)
					setCatalogState({ status: "ready", data: data || {} });
			})
			.catch((err) => {
				toastApiError(err, "Failed to load Iris field catalog");
				if (mountedRef.current)
					setCatalogState({ status: "error", data: null });
			});
	}, [api, mountedRef]);

	useEffect(() => {
		if (step >= 1 && catalogState.status === "idle") fetchCatalog();
	}, [step, catalogState.status, fetchCatalog]);

	// ---- Options (GET /import/iris/options), fetched when grouping step opens ----
	const fetchOptions = useCallback(() => {
		setOptionsState({ status: "loading", data: null });
		api
			.getIrisOptions()
			.then((data) => {
				if (mountedRef.current)
					setOptionsState({ status: "ready", data: data || {} });
			})
			.catch((err) => {
				toastApiError(err, "Failed to load Iris options");
				if (mountedRef.current)
					setOptionsState({ status: "error", data: null });
			});
	}, [api, mountedRef]);

	useEffect(() => {
		if (step >= 2 && optionsState.status === "idle") fetchOptions();
	}, [step, optionsState.status, fetchOptions]);

	const { fields: irisFields, fieldById } = useMemo(
		() => normalizeCatalog(catalogState.data),
		[catalogState.data]
	);
	const associationOptions = useMemo(
		() => associationOptionsFromCatalog(catalogState.data),
		[catalogState.data]
	);
	const dbaFieldId = useMemo(
		() => dbaFieldIdFromCatalog(catalogState.data),
		[catalogState.data]
	);

	// Columns that can never be imported (scrub metadata / a copy of EXISTING
	// Iris data, or empty in every row) — kept out of every column list the
	// wizard shows or offers for mapping, and surfaced instead as a collapsed
	// note (MapColumnsStep) so nothing is silently lost.
	const hiddenCols = useMemo(() => hiddenColumns(upload), [upload]);
	const visibleCols = useMemo(() => visibleColumns(upload), [upload]);

	// ---- Change 4: Group & Preview preselections (one-shot, user overridable) ----
	useEffect(() => {
		if (step !== 2 || groupByPreselectedRef.current) return;
		// Wait for the catalog so the `dba`-mapped column can win over the
		// header-name fallback; an error state still gets the fallback.
		if (catalogState.status === "idle" || catalogState.status === "loading") return;
		groupByPreselectedRef.current = true;
		if (groupBy.length > 0) return;
		const column = suggestGroupByColumn(visibleCols, mapping, dbaFieldId);
		if (column) setGroupBy([column]);
	}, [step, catalogState.status, groupBy.length, visibleCols, mapping, dbaFieldId]);

	useEffect(() => {
		if (optionsState.status !== "ready" || defaultsPreselectedRef.current) return;
		defaultsPreselectedRef.current = true;
		setDefaults((prev) => {
			const next = { ...prev };
			if (!prev.status) {
				const id = optionIdByName(
					optionsState.data?.statuses,
					DEFAULT_STATUS_NAME
				);
				if (id !== null) next.status = String(id);
			}
			if (!prev.group) {
				const id = optionIdByName(optionsState.data?.groups, DEFAULT_GROUP_NAME);
				if (id !== null) next.group = String(id);
			}
			return next;
		});
	}, [optionsState]);

	// ---- Handlers ----
	const handleUploaded = useCallback((result, name) => {
		setUpload(result);
		setFileName(name);
		setMapping(normalizeMappingObject(result.suggested_mapping));
		groupByPreselectedRef.current = false;
		setGroupBy([]);
		setPrimaryFlagColumn("");
		setAssociation("");
		setAssignedUserId(null);
		setStrictGrouping(false);
		setPreview(null);
		setBatchId(null);
	}, []);

	// A list handed over from the scrub results panel (a stored green list or
	// the user's own reviewed file) — already staged server-side, so it goes
	// straight to Map Columns exactly as if it had been uploaded here.
	useEffect(() => {
		if (!handoff?.upload) return;
		handleUploaded(handoff.upload, handoff.fileName);
		setStep(1);
	}, [handoff, handleUploaded]);

	const setColumnField = useCallback(
		(column, fieldId) => {
			setMapping((prev) => {
				const next = { ...prev };
				if (fieldId === null || fieldId === undefined) {
					delete next[column];
					return next;
				}
				const field = fieldById[String(fieldId)];
				const prevEntry = prev[column];
				const scope = prevEntry
					? prevEntry.scope
					: field && field.isRecordSet
					? "contact"
					: "lead";
				const entry = { field_id: fieldId, scope };
				// Keep the paired Contacts target across a lead-field change (the
				// user is retargeting the lead half, not the contact half); a
				// record-set field is itself the contact half. `via` is dropped —
				// this is now the user's own choice, not a suggestion.
				if (
					scope === "lead" &&
					prevEntry &&
					prevEntry.contact_field_id !== null &&
					prevEntry.contact_field_id !== undefined
				) {
					entry.contact_field_id = prevEntry.contact_field_id;
				}
				// Same for a full-name split: retargeting the lead half leaves the
				// two Contacts halves alone.
				if (scope === "lead" && prevEntry && prevEntry.contact_split) {
					entry.contact_split = prevEntry.contact_split;
				}
				next[column] = entry;
				return next;
			});
		},
		[fieldById]
	);

	// The paired Contacts record-set field (dual placement). null clears it.
	const setColumnContactField = useCallback((column, fieldId) => {
		setMapping((prev) => {
			if (!prev[column]) return prev;
			const entry = { ...prev[column] };
			if (fieldId === null || fieldId === undefined) delete entry.contact_field_id;
			else entry.contact_field_id = fieldId;
			return { ...prev, [column]: entry };
		});
	}, []);

	// Turn off a full-name column's Contacts First/Last split (opt-out). The
	// column then has no contact-side target at all, like any plain lead column.
	const clearColumnContactSplit = useCallback((column) => {
		setMapping((prev) => {
			if (!prev[column] || !prev[column].contact_split) return prev;
			const entry = { ...prev[column] };
			delete entry.contact_split;
			return { ...prev, [column]: entry };
		});
	}, []);

	const setColumnScope = useCallback((column, scope) => {
		setMapping((prev) => {
			if (!prev[column]) return prev;
			const entry = { ...prev[column], scope };
			// A contact-scope column already writes one field per row — pairing it
			// (or splitting it) on top would write the same value twice.
			if (scope === "contact") {
				delete entry.contact_field_id;
				delete entry.contact_split;
			}
			return { ...prev, [column]: entry };
		});
	}, []);

	const applyTemplate = useCallback(
		(tpl) => {
			const cols = upload?.columns || [];
			const rawMapping = normalizeMappingObject(tpl.mapping);
			const nextMapping = {};
			const droppedCols = [];
			Object.entries(rawMapping).forEach(([col, entry]) => {
				if (cols.includes(col)) nextMapping[col] = entry;
				else droppedCols.push(col);
			});
			setMapping(nextMapping);
			const rawGroupBy = Array.isArray(tpl.group_by) ? tpl.group_by : [];
			setGroupBy(rawGroupBy.filter((c) => cols.includes(c)));
			toast.info(`Loaded template "${tpl.name}".`);
			if (droppedCols.length > 0) {
				toast.warn(
					`Template columns not in this CSV were skipped: ${droppedCols.join(
						", "
					)}`
				);
			}
		},
		[upload]
	);

	const addGroupBy = useCallback((col) => {
		setGroupBy((prev) => (prev.includes(col) ? prev : [...prev, col]));
	}, []);

	const removeGroupBy = useCallback((col) => {
		setGroupBy((prev) => prev.filter((c) => c !== col));
	}, []);

	const setDefault = useCallback((key, value) => {
		setDefaults((prev) => ({ ...prev, [key]: value }));
	}, []);

	// Body for POST /import/preview and /import/execute (spec §5).
	const buildPayload = useCallback(() => {
		const payload = {
			upload_id: upload?.upload_id,
			mapping,
			group_by: groupBy,
			defaults: {
				campaign: coerceId(defaults.campaign),
				status: coerceId(defaults.status),
				source: coerceId(defaults.source),
				group: coerceId(defaults.group),
			},
			// Change 1: false (default) = normalized company matching; true =
			// strict exact-text match. Always sent (not optional).
			strict_grouping: strictGrouping,
		};
		// Not pinned by §5 — sent as an optional extra key; §4.3 requires the
		// concept ("primary flag column"). Backend may ignore it.
		if (primaryFlagColumn) payload.primary_flag_column = primaryFlagColumn;
		if (association) payload.association = association;
		// Single selection in the UI, list-shaped on the wire. Omitted entirely
		// when unassigned so Iris keeps its own default behavior.
		if (assignedUserId !== null && assignedUserId !== undefined) {
			payload.assigned_users = [Number(assignedUserId)];
		}
		return payload;
	}, [upload, mapping, groupBy, defaults, primaryFlagColumn, association,
		assignedUserId, strictGrouping]);

	const runPreview = useCallback(async () => {
		setPreviewLoading(true);
		try {
			const raw = await api.preview(buildPayload());
			if (!mountedRef.current) return;
			const norm = normalizePreview(raw);
			setPreview(norm);
			if (norm.blocking) {
				toast.warn(
					"Preview finished with blocking issues — fix them before executing."
				);
			} else {
				toast.success(
					`✅ Preview ready: ${norm.groups.length} lead${
						norm.groups.length === 1 ? "" : "s"
					} from ${norm.totalContacts} contact row${
						norm.totalContacts === 1 ? "" : "s"
					}.`
				);
			}
		} catch (err) {
			toastApiError(err, "Preview failed");
		} finally {
			if (mountedRef.current) setPreviewLoading(false);
		}
	}, [api, buildPayload, mountedRef]);

	const restart = useCallback(() => {
		setStep(0);
		setUpload(null);
		setFileName("");
		setMapping({});
		groupByPreselectedRef.current = false;
		defaultsPreselectedRef.current = false;
		setGroupBy([]);
		setPrimaryFlagColumn("");
		setAssociation("");
		setAssignedUserId(null);
		setStrictGrouping(false);
		setDefaults(EMPTY_DEFAULTS);
		setPreview(null);
		setBatchId(null);
	}, []);

	const mappedCount = Object.keys(mapping).length;

	return (
		<div className="importCard">
			{/* Stepper */}
			<div className="wizardStepper">
				{STEP_LABELS.map((label, i) => {
					// Once a batch is running, lock navigation to avoid double-execute.
					const clickable = batchId === null && i < step;
					return (
						<React.Fragment key={label}>
							{i > 0 && <span className="wizardStepArrow">›</span>}
							<button
								type="button"
								className={`wizardStep ${i === step ? "active" : ""} ${
									i < step ? "done" : ""
								} ${clickable ? "clickable" : ""}`}
								onClick={clickable ? () => setStep(i) : undefined}>
								<span className="wizardStepIndex">
									{i < step ? "✓" : i + 1}
								</span>
								{label}
							</button>
						</React.Fragment>
					);
				})}
			</div>

			{step === 0 && (
				<UploadStep
					api={api}
					upload={upload}
					fileName={fileName}
					onUploaded={handleUploaded}
					onNext={() => setStep(1)}
				/>
			)}

			{step === 1 && (
				<MapColumnsStep
					api={api}
					columns={visibleCols}
					hidden={hiddenCols}
					sampleRows={upload?.sample_rows || []}
					columnStats={upload?.column_stats || {}}
					mapping={mapping}
					onSetField={setColumnField}
					onSetContactField={setColumnContactField}
					onClearContactSplit={clearColumnContactSplit}
					onSetScope={setColumnScope}
					fields={irisFields}
					catalogStatus={catalogState.status}
					onRetryCatalog={fetchCatalog}
					groupBy={groupBy}
					onApplyTemplate={applyTemplate}
					onBack={() => setStep(0)}
					onNext={() => setStep(2)}
				/>
			)}

			{step === 2 && (
				<GroupPreviewStep
					columns={visibleCols}
					groupBy={groupBy}
					onAddGroupBy={addGroupBy}
					onRemoveGroupBy={removeGroupBy}
					primaryFlagColumn={primaryFlagColumn}
					onSetPrimaryFlagColumn={setPrimaryFlagColumn}
					strictGrouping={strictGrouping}
					onSetStrictGrouping={setStrictGrouping}
					defaults={defaults}
					onSetDefault={setDefault}
					association={association}
					onSetAssociation={setAssociation}
					associationOptions={associationOptions}
					assignedUserId={assignedUserId}
					onSetAssignedUserId={setAssignedUserId}
					options={optionsState.data}
					optionsStatus={optionsState.status}
					onRetryOptions={fetchOptions}
					preview={preview}
					previewLoading={previewLoading}
					onRunPreview={runPreview}
					onBack={() => setStep(1)}
					onNext={() => setStep(3)}
				/>
			)}

			{step === 3 && (
				<ExecuteStep
					api={api}
					fileName={fileName}
					preview={preview}
					buildPayload={buildPayload}
					defaults={defaults}
					association={association}
					assignedUserId={assignedUserId}
					strictGrouping={strictGrouping}
					options={optionsState.data}
					groupBy={groupBy}
					mappedCount={mappedCount}
					batchId={batchId}
					onBatchStarted={setBatchId}
					onViewHistory={onViewHistory}
					onRestart={restart}
					onBack={() => setStep(2)}
				/>
			)}
		</div>
	);
}
